/**
 * Asset and liability type definitions used by the calculation engine.
 * Ported from frontend/src/constants/assetTypes.js — deliberately omits the `icon` field
 * (lucide-react, UI-only). `assetDisplayName` IS needed — the Support at Home suggestion-engine
 * evaluator (suggestionEngine/supportAtHome.ts, ported from strategy/SupportAtHomeStrategyEvaluator.js)
 * uses it to label asset-allocation rows.
 */

export interface AssetTypeDef {
    id: string;
    name: string;
    isLiquid?: boolean;
    hasCgt: boolean;
    individualOnly?: boolean;
}

export const ASSET_TYPES: AssetTypeDef[] = [
    { id: 'bankAccounts', name: 'Bank Accounts / Cash', isLiquid: true, hasCgt: false },
    { id: 'termDeposits', name: 'Term Deposits', isLiquid: true, hasCgt: false },
    { id: 'investmentProperty', name: 'Investment Property', isLiquid: false, hasCgt: true },
    { id: 'shares', name: 'Shares / Managed Funds', isLiquid: true, hasCgt: true },
    /*
     * An investment (insurance) bond: taxed inside at 30%, tax-free in the holder's hands after ten
     * years, counted in full and deemed by Centrelink. Not liquid for the engine's purposes — the
     * projection leaves it untouched until its ten years are up (investmentBondPlugin), and no CGT:
     * the life office has already paid the tax on the growth.
     */
    { id: 'investmentBonds', name: 'Investment / Insurance Bond', isLiquid: false, hasCgt: false },
    { id: 'superannuation', name: 'Superannuation (Accumulation)', individualOnly: true, isLiquid: true, hasCgt: false },
    { id: 'accountBasedPensions', name: 'Account Based Pension (Super)', individualOnly: true, isLiquid: true, hasCgt: false },
    { id: 'annuities', name: 'Annuities', individualOnly: true, isLiquid: false, hasCgt: false },
    { id: 'funeralBonds', name: 'Funeral Bonds', individualOnly: true, isLiquid: false, hasCgt: false },
    /*
     * A funeral paid for in advance with a funeral director (owner, 5 Oct 2026). Not a funeral bond:
     * a bond is an investment, exempt only up to the indexed limit; a prepaid funeral is exempt from
     * both means tests with NO cap, provided it is a contract for future services and not refundable.
     */
    { id: 'prepaidFuneral', name: 'Prepaid Funeral (with a funeral director)', individualOnly: true, isLiquid: false, hasCgt: false },
    /*
     * Money lent to someone else — usually family (owner, 5 Oct 2026). A financial asset: counted at
     * the balance still owed (not the interest), and deemed, whether or not it is being repaid.
     * Not liquid: the projection cannot sell a loan to fund anything.
     */
    { id: 'loansToOthers', name: 'Money Lent to Others', isLiquid: false, hasCgt: false },
    { id: 'personalAssets', name: 'Personal Assets (e.g. cars, contents etc.)', isLiquid: false, hasCgt: false },
    { id: 'other', name: 'Other Asset', isLiquid: false, hasCgt: true },
];

export const LIABILITY_TYPES: AssetTypeDef[] = [
    { id: 'mortgages', name: 'Mortgage / Home Loan', hasCgt: false },
    { id: 'personalLoans', name: 'Personal Loans', hasCgt: false },
    { id: 'creditCards', name: 'Credit Card Debt', hasCgt: false },
    /*
     * A reverse mortgage belongs here with every other debt, and until 2026-08-22 it had nowhere to
     * live. The only place to record one was a question on the Living Arrangements step
     * (`home.commercialReverseMortgageAmount`) which was written to the fact-find and then READ BY
     * NOTHING: not net worth, not the means test, not any projection. A client with a $200,000
     * reverse mortgage had their estate overstated by $200,000 and no screen showed the debt.
     *
     * Recorded as a liability it is counted in net worth automatically (assessNetWorth sums every
     * liability bucket), and correctly draws no repayment schedule — `isOutsidePersonalCashflow`
     * already excludes `reverse_mortgage` because the interest capitalises rather than being
     * serviced. `reverseMortgageBalanceGrowth` in loanPlugin compounds it across a projection.
     */
    { id: 'reverseMortgages', name: 'Reverse Mortgage', hasCgt: false },
    /*
     * The Home Equity Access Scheme, for the same reason and with the same history: an existing HEAS
     * loan could only be recorded on the Living Arrangements step, where `home.equityReleaseAmount`
     * was written and read by nothing at all. Government-set rate, capitalising like a reverse
     * mortgage, and equally invisible to every calculation until now.
     */
    { id: 'homeEquityAccessScheme', name: 'Home Equity Access Scheme (HEAS)', hasCgt: false },
];

export const assetDisplayName = (asset: any, assetType: string): string => {
    const desc = (asset?.description || asset?.name || '').trim();
    if (desc) return desc;
    return ASSET_TYPES.find((t) => t.id === assetType)?.name || LIABILITY_TYPES.find((t) => t.id === assetType)?.name || 'Account';
};

export const assetTypeHasCgt = (assetType: string): boolean => !!ASSET_TYPES.find((t) => t.id === assetType)?.hasCgt;
